import React from 'react'
import classNames from 'classnames'
import { useIntl } from 'react-intl'

import { MoreIcon } from '@slite/icons'

import { IconButton } from '../IconButton/IconButton'
import { PopoverOnClick } from '../PopoverOnClick/PopoverOnClick'
import { DEFAULT_MAX_ITEMS_WITHOUT_POPOVER } from './constants'

export type BreadcrumbProps = {
  className?: string
  children: React.ReactNode
  disablePopover?: boolean
  maxItemsWithoutPopover?: number
  ref?: React.Ref<HTMLDivElement>
}

export function Breadcrumb({
  className,
  children,
  disablePopover,
  maxItemsWithoutPopover = DEFAULT_MAX_ITEMS_WITHOUT_POPOVER,
  ref,
}: BreadcrumbProps) {
  const intl = useIntl()
  const childrenArray = React.Children.toArray(children)
  const shouldUsePopover = childrenArray.length > maxItemsWithoutPopover

  return (
    <div
      ref={ref}
      className={classNames(
        className,
        'line-height-large font-family-system',
        'flexbox flexbox-grow flexbox-align-center',
        'ellipsis',
        {
          'font-weight-semibold': !className?.includes('font-weight-'),
        }
      )}
      style={{ minWidth: 0 }} // ensure ellipsis works for nested flex children
    >
      {childrenArray.map((child, index) => {
        if (!shouldUsePopover) {
          return (
            <React.Fragment key={index}>
              {index > 0 && (
                <span
                  className="shade-40 font-size-large"
                  style={{ margin: '0 10px' }}
                >
                  {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
                  {'/'}
                </span>
              )}
              {child}
            </React.Fragment>
          )
        }

        const isFirstItem = index === 0
        const numLastItemsToShow = maxItemsWithoutPopover - 1
        const lastItemsStartIndex = childrenArray.length - numLastItemsToShow
        const isLastItems = index >= lastItemsStartIndex
        const shouldShowPopover = index === 1
        const shouldHideItem = !isFirstItem && !isLastItems && index !== 1

        if (shouldShowPopover) {
          return (
            <div key={index} className="flexbox flexbox-align-center">
              <span
                className="shade-40 font-size-large"
                style={{ margin: '0 10px' }}
              >
                {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
                {'/'}
              </span>
              {disablePopover ? (
                <MoreIcon className="rotate-90" style={{ margin: '0 -4px' }} />
              ) : (
                <PopoverOnClick
                  positions={['bottom', 'left']}
                  reposition
                  zIndex="7"
                  content={({ ref }) => (
                    <Breadcrumb
                      ref={ref}
                      className="border-small radius-large border-shade-50 padding-vertical-8 padding-horizontal-12 shadow-medium background-shade-100"
                      disablePopover
                    >
                      {childrenArray.slice(1, lastItemsStartIndex)}
                    </Breadcrumb>
                  )}
                  target={({ ref, toggle, isOpen }) => (
                    <IconButton
                      size="small"
                      variant="tertiary"
                      isActive={isOpen}
                      icon={MoreIcon}
                      ref={ref}
                      className="rotate-90"
                      style={{ margin: '0 -4px' }}
                      aria-label={intl.formatMessage({
                        id: 'components.breadcrumb.showFullPath',
                        defaultMessage: 'Show full path',
                      })}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        toggle()
                      }}
                      iconSize="medium"
                    />
                  )}
                />
              )}
            </div>
          )
        }
        if (shouldHideItem) {
          //these are elements rendered in the popover
          return null
        }
        return (
          <React.Fragment key={index}>
            {index > 0 && (
              <span
                className="shade-40 font-size-large"
                style={{ margin: '0 10px' }}
              >
                {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
                {'/'}
              </span>
            )}
            {child}
          </React.Fragment>
        )
      })}
    </div>
  )
}
